import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Circle, ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { payrollApi } from "../../services/payrollApi";

/** Payroll Source Lineage (Payroll & Leave Deep Audit item 7 - mandatory). Shows the full
 * chain from Published Schedule through Payment Confirmation and lets a manager jump straight
 * back to the locked source timecard - never shows a downstream stage as COMPLETE when the
 * real event hasn't happened (e.g. Published Payslip only completes when the PERIOD itself is
 * PUBLISHED, never inferred from a calculated/reviewed line alone). */
export function PayrollSourceLineage({ line, period, canOpenTimecard, testId }) {
  const navigate = useNavigate();
  const hasPendingAdjustments = (line.adjustments || []).some((a) => a.status === "PENDING");
  const hasReviewFlags = (line.shift_premium_review_flags || []).length > 0;

  const stages = [
    {
      key: "schedule", label: "Published Schedule", status: "REFERENCE",
      detail: "Feeds the scheduled-vs-actual comparison on the Timekeeping Review page",
      action: canOpenTimecard ? () => navigate(`/management/timekeeping/review/${line.employee_id}?payroll_month=${period.payroll_month}`) : undefined,
      actionLabel: "View comparison",
    },
    {
      key: "timecard", label: "Approved Timecard", status: line.status === "BLOCKED" ? "MISSING" : "LOCKED_FOR_PAYROLL",
      detail: canOpenTimecard ? "Source BLUE/RED scans -> OCR -> reviewer corrections -> locked for payroll" : "Source BLUE/RED scans -> OCR -> reviewer corrections -> locked for payroll (Timekeeping Review access required to open)",
      action: canOpenTimecard ? () => navigate(`/management/timekeeping/review/${line.employee_id}?payroll_month=${period.payroll_month}`) : undefined,
      actionLabel: "Open locked timecard",
    },
    {
      key: "hours", label: "Approved Hours", status: line.status === "BLOCKED" ? "BLOCKED" : "COMPLETE",
      detail: line.status === "BLOCKED" ? (line.blockers || []).join(", ") : `${line.regular_hours || 0}h regular, ${line.late_night_hours || 0}h late-night`,
    },
    {
      key: "wage", label: "Wage Profile", status: line.wage_profile_id ? "APPROVED" : "MISSING",
      detail: line.wage_profile_id ? `¥${line.hourly_rate || "-"}/hr, effective-dated` : "No approved wage profile at calculation time",
      action: () => navigate(`/management/employees/${line.employee_id}/wage-history`),
      actionLabel: "View wage history",
    },
    {
      key: "calculation", label: "Payroll Calculation", status: line.status,
      detail: `Gross ¥${Number(line.gross_pay).toLocaleString()}`,
    },
    {
      key: "review", label: "Review", status: hasPendingAdjustments || hasReviewFlags ? "REVIEW_REQUIRED" : "CLEAR",
      detail: hasPendingAdjustments ? "Pending adjustment approval" : hasReviewFlags ? "Shift-premium review required" : "No open review items",
    },
    {
      key: "payslip", label: "Published Payslip", status: period.status === "PUBLISHED" ? "PUBLISHED" : "NOT_YET_PUBLISHED",
      detail: period.status === "PUBLISHED" ? "Visible to the employee in My Payroll" : "Only appears once the whole period is published",
      action: line.summary_report_path ? () => window.open(payrollApi.getLineReportUrl(line.id, "summary"), "_blank") : undefined,
      actionLabel: "View payslip PDF",
    },
    {
      key: "payment", label: "Payment Confirmation", status: period.status === "PUBLISHED" ? "SEE_PAYMENTS" : "NOT_READY",
      detail: "Manual bank-transfer evidence, confirmed by a second person",
      action: () => navigate(`/management/payroll/periods/${period.id}/payments`),
      actionLabel: "Open Payment Evidence",
    },
  ];

  const isComplete = (status) => ["LOCKED_FOR_PAYROLL", "COMPLETE", "APPROVED", "PUBLISHED", "CLEAR", "CALCULATED", "PUBLISHED_PERIOD"].includes(status) || status === "PUBLISHED";
  const isBlocked = (status) => ["MISSING", "BLOCKED", "REVIEW_REQUIRED"].includes(status);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Source Lineage</p>
      <div className="space-y-0">
        {stages.map((s, i) => (
          <div key={s.key} className="flex gap-3" data-testid={`${testId}-stage-${s.key}`}>
            <div className="flex flex-col items-center">
              <div className={cn(
                "h-6 w-6 rounded-full flex items-center justify-center shrink-0",
                isComplete(s.status) && "bg-emerald-100 text-emerald-700",
                isBlocked(s.status) && "bg-red-100 text-red-700",
                !isComplete(s.status) && !isBlocked(s.status) && "bg-gray-100 text-gray-500",
              )}>
                {isComplete(s.status) ? <Check size={13} /> : <Circle size={7} fill="currentColor" />}
              </div>
              {i < stages.length - 1 && <div className="w-px flex-1 bg-gray-200 my-1" />}
            </div>
            <div className="pb-4 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-800">{s.label}</p>
                <span className={cn(
                  "text-[10px] font-bold uppercase px-1.5 py-0.5 rounded",
                  isComplete(s.status) && "bg-emerald-50 text-emerald-700",
                  isBlocked(s.status) && "bg-red-50 text-red-700",
                  !isComplete(s.status) && !isBlocked(s.status) && "bg-gray-100 text-gray-500",
                )}>{s.status.replaceAll("_", " ")}</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{s.detail}</p>
              {s.action && (
                <button data-testid={`${testId}-action-${s.key}`} onClick={s.action} className="mt-1.5 text-xs font-semibold text-blue-600 hover:underline flex items-center gap-1">
                  {s.actionLabel} <ArrowRight size={11} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
